import React, { useState } from "react";
import { C, font } from "../../../lib/styles.js";
import { bridge } from "../../../lib/bridgeCallables.js";

/**
 * Traffic Control → Backups (SafeShare slice 5.3; docs/safeshare/design/
 * 02-traffic-control.md sections 3.3 and 6). For one job, one organization or
 * one capture's serial number: what is not safe yet. Each client file is
 * red (in no cloud place yet), amber (in one) or green (verified in two:
 * the client's cloud place and the Suite's archive), with the backup and
 * read-back orders working on it. Read-only; the same projection as the
 * Transfers tab (getFileCopies, admin only).
 *
 * A guest capture's files never appear here: they never leave the guest
 * bucket, so they have no copy rows.
 */

export const COLOURS = { red: "#B3261E", amber: "#8A5A00", green: "#2E7D32" };
const WORDS = { red: "Not in the cloud yet", amber: "In one cloud place", green: "Safe: two cloud places" };

/** One row per client file: its verified cloud places, its studio copies, and its colour. */
export const backupRows = (data) => {
  const rows = new Map();
  for (const c of data?.copies || []) {
    const key = `${c.sn || c.sharedVersionId}|${c.versionId}|${c.fileName}`;
    if (!rows.has(key)) rows.set(key, { key, sn: c.sn || c.sharedVersionId, versionId: c.versionId, fileName: c.fileName, cloud: new Set(), studio: 0, suspect: false, bytes: null });
    const r = rows.get(key);
    if (c.placeKind === "cloud" && c.state === "verified") r.cloud.add(c.place);
    if ((c.placeKind === "local" || c.placeKind === "nas") && c.state === "verified" && c.counts) r.studio += 1;
    if (c.state === "suspect") r.suspect = true;
    if (typeof c.bytes === "number") r.bytes = c.bytes;
  }
  const out = [...rows.values()].map((r) => {
    const colour = r.cloud.size >= 2 ? "green" : r.cloud.size === 1 ? "amber" : "red";
    return { ...r, cloudPlaces: [...r.cloud].map((p) => data?.places?.[p]?.label || p), colour };
  });
  const rank = { red: 0, amber: 1, green: 2 };
  return out.sort((a, b) => rank[a.colour] - rank[b.colour] || String(a.sn).localeCompare(String(b.sn)) || a.fileName.localeCompare(b.fileName));
};

/** The backup work in flight or failed lately (backups, and the read-backs that fill a capture's sha256). */
export const backupOrders = (data) => (data?.orders || []).filter((o) => o.kind === "backup" || o.kind === "verify");

export const summaryOf = (rows) => rows.reduce((s, r) => ({ ...s, [r.colour]: (s[r.colour] || 0) + 1 }), { red: 0, amber: 0, green: 0 });

export function BackupsTable({ data }) {
  const rows = backupRows(data);
  if (!rows.length) return <div style={S.muted}>No client files recorded for this yet.</div>;
  const sum = summaryOf(rows);
  return (
    <div>
      <div style={{ display: "flex", gap: 16, margin: "4px 0 12px" }}>
        {["red", "amber", "green"].map((k) => (
          <div key={k} style={{ color: COLOURS[k], fontWeight: 500 }}>{sum[k]} {WORDS[k].toLowerCase()}</div>
        ))}
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={S.table}>
          <thead>
            <tr>{["File", "Cloud", "Studio copies", ""].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <td style={S.td}>
                  <div style={{ fontWeight: 500 }}>{r.fileName}</div>
                  <div style={S.small}>{r.sn} · {r.versionId}</div>
                </td>
                <td style={S.td}>{r.cloudPlaces.join(", ") || "—"}</td>
                <td style={S.td}>{r.studio || "—"}</td>
                <td style={{ ...S.td, color: COLOURS[r.colour], fontWeight: 500 }}>
                  {WORDS[r.colour]}{r.suspect ? " · a copy is suspect" : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function BackupOrders({ orders }) {
  const list = backupOrders({ orders });
  if (!list.length) return null;
  return (
    <section style={{ marginTop: 22 }}>
      <div style={S.h2}>Backup work</div>
      <table style={S.table}>
        <thead>
          <tr>{["Kind", "Machine", "File", "State", "Progress"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {list.map((o) => (
            <tr key={o.orderId}>
              <td style={S.td}>{o.kind === "verify" ? "read-back or check" : o.kind}</td>
              <td style={S.td}>{o.machine}</td>
              <td style={S.td}>{o.fileName || "—"}</td>
              <td style={S.td}>{o.state}{o.error ? ` (${o.error})` : ""}</td>
              <td style={S.td}>{o.progress && o.progress.bytesTotal ? `${Math.round((o.progress.bytesDone / o.progress.bytesTotal) * 100)}%` : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

export default function BackupsView() {
  const [query, setQuery] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async (e) => {
    e?.preventDefault?.();
    const q = query.trim();
    if (!q) return;
    setLoading(true);
    setError(null);
    try {
      const by = /^[a-z0-9]{6}\d{10}$/.test(q) ? { sn: q } : /^org_[0-9a-f]{16}$/.test(q) ? { orgId: q } : { jobId: q };
      setData(await bridge.getFileCopies(by));
    } catch (err) {
      setData(null);
      setError(err?.code === "functions/permission-denied" ? "Backups are visible to admins only." : (err?.message || "Could not load"));
    } finally {
      setLoading(false);
    }
  };
  return (
    <div style={S.page}>
      <div style={S.h1}>Backups</div>
      <div style={S.muted}>What is not safe yet: each client file in no cloud place (red), one (amber) or two (green). Read-only.</div>
      <form onSubmit={load} style={{ display: "flex", gap: 8, margin: "14px 0 18px" }}>
        <input aria-label="Job id, organization or serial number" value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Job id, organization or serial number" style={S.input} />
        <button type="submit" style={S.button} disabled={loading}>{loading ? "Loading…" : "Show"}</button>
      </form>
      {error && <div style={{ ...S.muted, color: COLOURS.red }}>{error}</div>}
      {data && data.complete?.copies === false && (
        <div style={{ ...S.muted, color: COLOURS.amber, marginBottom: 8 }}>Not every file is shown: there are more copies than one page holds.</div>
      )}
      {data && <BackupsTable data={data} />}
      {data && <BackupOrders orders={data.orders} />}
    </div>
  );
}

const S = {
  page: { padding: "24px 28px", fontFamily: font, color: C.text, fontSize: 13, maxWidth: 1200, margin: "0 auto", boxSizing: "border-box" },
  h1: { fontSize: 18, fontWeight: 500, marginBottom: 4 },
  h2: { fontSize: 15, fontWeight: 500, marginBottom: 8 },
  muted: { fontSize: 12.5, color: C.muted, fontWeight: 300 },
  small: { fontSize: 11, color: C.muted, fontWeight: 300 },
  input: { flex: "1 1 auto", maxWidth: 320, padding: "6px 10px", fontFamily: font, fontSize: 13, border: `1px solid ${C.border || "#ddd"}`, borderRadius: 6 },
  button: { padding: "6px 14px", fontFamily: font, fontSize: 13, borderRadius: 6, border: `1px solid ${C.border || "#ddd"}`, background: C.surfaceAlt || "#f5f5f5", cursor: "pointer" },
  table: { borderCollapse: "collapse", width: "100%", fontSize: 12.5 },
  th: { textAlign: "left", fontWeight: 500, padding: "6px 10px", borderBottom: `1px solid ${C.border || "#ddd"}`, whiteSpace: "nowrap" },
  td: { padding: "6px 10px", borderBottom: `1px solid ${C.border || "#eee"}`, verticalAlign: "top" },
};
